<template>
    <client-only>
        <button
            type="button"
            data-cy="toggleWishlist"
            class="btn z-10"
            :class="[{'border-primary' : isOnWishlist}, onBuyBox ? 'btn-square btn-secondary' : 'btn-ghost btn-circle bg-transparent border-0 shadow-none absolute top-1 right-1']"
            :aria-label="isOnWishlist ? $t('wishlist.product.toggleButton.removeLabel') : $t('wishlist.product.toggleButton.addLabel')"
            @click.prevent="toggleWishlist"
        >
            <MiscIcon v-show="isOnWishlist && !loading" name="heart-filled" />
            <MiscIcon v-show="!isOnWishlist && !loading" name="heart" />
            <span v-show="loading" class="loading loading-spinner h-5 w-5" />
            <slot />
        </button>

        <template #fallback>
            <div class="btn btn-ghost" :class="onBuyBox ? 'btn-square' : 'btn-circle absolute top-3 right-3'">
                <MiscIcon name="heart" />
            </div>
        </template>
    </client-only>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { useProductWishlist } from '#imports'

const props = defineProps<{
    product: Pick<Schemas['Product'], 'id'>,
    onBuyBox?: boolean
}>()

const { t } = useI18n()
const { showNotification } = useNotification()
const nuxtApp = useNuxtApp()

const { isInWishlist, addToWishlist, removeFromWishlist } = useProductWishlist(() => props.product.id)

const loading = ref(false)
const isOnWishlist = computed(() => isInWishlist.value)

async function toggleWishlist() {
    loading.value = true
    try {
        if (!isInWishlist.value) {
            await addToWishlist()
            showNotification(t('wishlist.product.toggleButton.addedToWishlist'), 'success')
            nuxtApp.callHook('addToWishlist', { product: props.product })
        } else {
            await removeFromWishlist()
            showNotification(t('wishlist.product.toggleButton.removedFromWishlist'), 'success')
            nuxtApp.callHook('removeFromWishlist', { product: props.product })
        }
    } finally {
        loading.value = false
    }
}
</script>
